'use client';

// This component must be present in a page's layout for useMfeLoadStore and any
// hook that depends on it (e.g. useOpenMfeModal) to function correctly.
// See the JSDoc on useMfeLoadStore for current page coverage and guidance on
// extending it to additional layouts.

import { eventNames, useCoreEvents } from '@carvana/core-events';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback } from 'react';
import {
  ALL_MFE_DEFINITIONS,
  isFromMfeSource,
  type MfeDefinition
} from '~/constants/common/MfeModules';
import { useMfeLoadStore } from '~/state/common/MfeLoadState';
import { rotateSearchSessionId } from '~/utilities/common/searchSessionIdCookie';

const REPLAY_OPTIONS = { withHistory: true };

const datadogActionPrefixByModule = new Map(
  ALL_MFE_DEFINITIONS.flatMap(({ datadogActionPrefix, moduleName }) =>
    datadogActionPrefix ? [[moduleName, datadogActionPrefix] as const] : []
  )
);

type MfeReadinessRecorderProps = {
  definition: MfeDefinition;
};

// MFER_EVENTS 'loaded' fires when the MFE bundle registers in globalThis — before its React
// tree renders or initialises — so each MFE's own ready event is recorded as 'ready' instead.
// Pre-REO-8289: once the framework ships markReady/teardown props, MFEs will emit
// MFER_EVENTS 'ready' directly and these recorders can be removed.
function MfeReadinessRecorder({ definition }: MfeReadinessRecorderProps) {
  const record = useMfeLoadStore(state => state.record);
  const {
    datadogActionPrefix,
    moduleName,
    readyEvent,
    readySource,
    replayReady,
    unreadyEvent
  } = definition;

  const handleReady = useCallback(
    (payload: unknown) => {
      if (!isFromMfeSource(payload, readySource)) return;

      if (datadogActionPrefix) {
        datadogRum.addAction(`${datadogActionPrefix}-app-ready`, {
          componentName: 'default'
        });
      }

      record({
        componentName: 'default',
        data: 'ready',
        moduleName,
        timestamp: Date.now()
      });
    },
    [datadogActionPrefix, moduleName, readySource, record]
  );

  const handleUnready = useCallback(
    (payload: unknown) => {
      if (!isFromMfeSource(payload, readySource)) return;

      record({
        componentName: 'default',
        data: 'teardown',
        moduleName,
        timestamp: Date.now()
      });
    },
    [moduleName, readySource, record]
  );

  useCoreEvents(
    readyEvent,
    handleReady,
    replayReady ? REPLAY_OPTIONS : undefined
  );
  useCoreEvents(unreadyEvent, handleUnready);

  return null;
}

export default function MfeEventsListener() {
  const record = useMfeLoadStore(state => state.record);

  const handleMfeEvent = useCallback(
    (event: {
      componentName?: string;
      data?: string;
      eventName?: string;
      moduleName?: string;
    }) => {
      if (
        event.eventName === 'react-component' &&
        event.componentName &&
        event.data &&
        event.moduleName
      ) {
        const datadogActionPrefix = datadogActionPrefixByModule.get(
          event.moduleName
        );

        if (event.data === 'error' || event.data === 'timeout') {
          datadogRum.addError(
            new Error(`MFE registry reported ${event.data}`),
            {
              componentName: event.componentName,
              location: 'MfeEventsListener',
              moduleName: event.moduleName
            }
          );
        } else if (datadogActionPrefix) {
          // Softpull's readiness is intermittently silent in production — log its
          // registry-level lifecycle so a 'loaded' with no later 'ready' is visible.
          datadogRum.addAction(
            `${datadogActionPrefix}-registry-${event.data}`,
            {
              componentName: event.componentName,
              moduleName: event.moduleName
            }
          );
        }

        record({
          componentName: event.componentName,
          data: event.data as 'error' | 'loaded' | 'timeout',
          moduleName: event.moduleName,
          timestamp: Date.now()
        });
      }
    },
    [record]
  );

  const handleSoftPullSuccess = useCallback(() => {
    rotateSearchSessionId();
  }, []);

  useCoreEvents(eventNames.mfer.events, handleMfeEvent);
  useCoreEvents(eventNames.softpullModal.success, handleSoftPullSuccess);

  return (
    <>
      {ALL_MFE_DEFINITIONS.map(definition => (
        <MfeReadinessRecorder
          definition={definition}
          key={definition.moduleName}
        />
      ))}
    </>
  );
}
